import { IconButton, Menu, MenuButton, MenuItem, MenuList, Portal } from '@chakra-ui/react';
import { ChevronDown, ChevronUp, MoreHorizontal, Trash2 } from 'lucide-react';
import { iconSize } from '@duke-hq/ui/constants/scalable';
import { MENU_Z_INDEX } from './AnchoredPanel';

// -- ItemActionsMenu -----------------------------------------------------------
// The shared per-item overflow menu used by both conversation messages and
// option pills, so the two read the same: Move up / Move down (disabled at the
// ends) and Delete. Reorder lives here as explicit actions because in-editor
// drag (ProseMirror) is too fiddly to feel good; options additionally keep
// their drag grip, but both expose the same menu.
export function ItemActionsMenu({
  label,
  onMoveUp,
  onMoveDown,
  onDelete,
  canMoveUp,
  canMoveDown,
  isVisible,
  deleteTestId,
}: {
  /** Accessible label for the trigger, e.g. "Message actions". */
  label: string;
  onMoveUp: () => void;
  onMoveDown: () => void;
  onDelete: () => void;
  canMoveUp: boolean;
  canMoveDown: boolean;
  /** Whether the (hover-revealed) trigger is shown. */
  isVisible: boolean;
  /** Optional test id on the Delete item (for e2e targeting). */
  deleteTestId?: string;
}) {
  return (
    <Menu>
      <MenuButton
        as={IconButton}
        aria-label={label}
        icon={<MoreHorizontal size={iconSize.lg} />}
        size="xs"
        variant="ghost"
        color="gray.400"
        opacity={isVisible ? 1 : 0}
        transition="opacity 0.15s"
      />
      {/* Portal the list to the document root so it escapes the node frame's
          clipping (otherwise the lower items, e.g. Delete on a low bubble, get
          cut off by the node's bottom edge). The items set an explicit neutral
          colour so they don't inherit the purple/brand colour the option pill
          puts on its surrounding text. */}
      <Portal>
        <MenuList zIndex={MENU_Z_INDEX} color="fg.default">
          <MenuItem icon={<ChevronUp size={16} />} isDisabled={!canMoveUp} onClick={onMoveUp}>
            Move up
          </MenuItem>
          <MenuItem icon={<ChevronDown size={16} />} isDisabled={!canMoveDown} onClick={onMoveDown}>
            Move down
          </MenuItem>
          <MenuItem icon={<Trash2 size={16} />} onClick={onDelete} data-testid={deleteTestId}>
            Delete
          </MenuItem>
        </MenuList>
      </Portal>
    </Menu>
  );
}
